import { AppearanceEditor } from "./AppearanceEditor";
import React, { useEffect, useState } from "react";
import {
  Save,
  Upload,
  Plus,
  Trash2,
  Copy,
  ExternalLink,
  ShieldCheck,
  UserPlus,
  Mail,
  RefreshCw,
  ArrowUpRight,
} from "lucide-react";
import {
  askConfirm,
  Button,
  Field,
  PageHead,
  Brand,
  Modal,
  Empty,
  download,
} from "./ui";
import { api, demo, config, supabase, rpc } from "./data";
import { safeUrl, uid, money } from "./domain";
export function Settings({ data, run, user, section = "general" }) {
  const [s, setS] = useState(structuredClone(data.store)),
    [busy, setBusy] = useState(false);
  const set = (k, v) => setS((s) => ({ ...s, [k]: v }));
  const role = data.members.find((m) => m.user_id === user.id)?.role;
  const canEdit = ["owner", "admin"].includes(role);
  const tabs = [
    ["general", "Datos de tienda"],
    ["appearance", "Aspecto y banner"],
    ["payments", "Pagos y entregas"],
    ["social", "Redes sociales"],
    ["pages", "Páginas"],
    ["blog", "Blog"],
    ["domain", "Dominio"],
  ];
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await run(() => api.saveStore(s));
    } catch {
    } finally {
      setBusy(false);
    }
  }
  async function upload(file, key) {
    if (!file) return;
    setBusy(true);
    try {
      const url = await api.upload(file, s.id);
      set(key, url);
    } catch (e) {
      run(() => Promise.reject(e)).catch(() => {});
    } finally {
      setBusy(false);
    }
  }
  if (!canEdit)
    return (
      <Empty
        title="Esta sección es para administradores"
        description="Tu rol de anfitrión permite gestionar productos, stock y pedidos."
      />
    );
  return (
    <>
      <PageHead
        eyebrow="DALE TU SELLO"
        title={
          section === "appearance"
            ? "Aspecto de tu tienda"
            : section === "payments"
              ? "Pagos y entregas"
              : "Configuración de tienda"
        }
        description="Guarda tus cambios y usa Ver tienda para revisar el resultado."
      />
      <div className="settings-tabs">
        {tabs.map(([key, title]) => (
          <a
            className={section === key ? "active" : ""}
            href={`#admin/settings/${key}`}
            key={key}
          >
            {title}
          </a>
        ))}
      </div>
      <form onSubmit={submit}>
        <div className="settings-grid">
          <div className="card">
            {section === "general" && (
              <>
                <h3>Información de Dilo Shop</h3>
                <Field
                  label="Nombre de tienda"
                  required
                  value={s.name}
                  onChange={(e) => set("name", e.target.value)}
                />
                <Field
                  label="WhatsApp de atención"
                  type="tel"
                  value={s.whatsapp}
                  onChange={(e) => set("whatsapp", e.target.value)}
                  placeholder="51999999999"
                  hint="Incluye el código de país, por ejemplo 51 para Perú."
                />
                <Field
                  label="Correo de atención"
                  type="email"
                  value={s.email}
                  onChange={(e) => set("email", e.target.value)}
                />
                <Field
                  label="Dirección de la tienda"
                  value={s.address}
                  onChange={(e) => set("address", e.target.value)}
                />
                <label className="check-line">
                  <input
                    type="checkbox"
                    checked={s.published}
                    onChange={(e) => set("published", e.target.checked)}
                  />{" "}
                  Tienda abierta al público
                </label>
              </>
            )}
            {section === "appearance" && <AppearanceEditor store={s} categories={data.categories} set={set} role={role} setBusy={setBusy} run={run}/>}
            {section === "payments" && (
              <>
                <h3>Datos para pagar</h3>
                <Field label="Yape: número y nombre del titular">
                  <textarea
                    value={s.yape}
                    onChange={(e) => set("yape", e.target.value)}
                    placeholder="Número: …\nTitular: …"
                  />
                </Field>
                <Field label="Plin: número y nombre del titular">
                  <textarea
                    value={s.plin}
                    onChange={(e) => set("plin", e.target.value)}
                  />
                </Field>
                <Field label="Transferencia: banco, cuenta, CCI y titular">
                  <textarea
                    value={s.bank}
                    onChange={(e) => set("bank", e.target.value)}
                  />
                </Field>
                <Field label="Instrucciones de pago">
                  <textarea
                    value={s.payment_instructions}
                    onChange={(e) =>
                      set("payment_instructions", e.target.value)
                    }
                  />
                </Field>
                <h3>Entregas</h3>
                <div className="form-grid">
                  <Field
                    label="Tarifa única de delivery (S/)"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={s.shipping_fee}
                    onChange={(e) =>
                      set("shipping_fee", Number(e.target.value))
                    }
                  />
                  <Field
                    label="Envío gratis desde (0 = desactivado)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={s.free_shipping}
                    onChange={(e) =>
                      set("free_shipping", Number(e.target.value))
                    }
                  />
                </div>
                <Field
                  label="Dirección para recojo"
                  value={s.address}
                  onChange={(e) => set("address", e.target.value)}
                />
                <p className="notice">
                  El total y el envío se calculan nuevamente en el servidor.
                  Confirma los pagos después de revisar el abono en Yape, Plin o
                  tu banco.
                </p>
              </>
            )}
            {section === "social" && (
              <>
                <h3>Conecta tus redes</h3>
                {["instagram", "facebook", "tiktok"].map((key) => (
                  <Field
                    key={key}
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    type="url"
                    value={s[key]}
                    onChange={(e) => set(key, e.target.value)}
                    placeholder="https://…"
                  />
                ))}
                <p className="notice">
                  Estos enlaces aparecen al pie de la tienda. Para cargar
                  productos de tus redes, utiliza las fotos originales o la
                  importación CSV de Productos.
                </p>
              </>
            )}
            {["pages", "blog"].includes(section) && (
              <>
                <h3>
                  {section === "pages"
                    ? "Páginas de información"
                    : "Publicaciones del blog"}
                </h3>
                <p className="muted">
                  {section === "pages"
                    ? "Puedes añadir políticas de compra, cambios, contacto o instrucciones de envío."
                    : "Los artículos se muestran en El diario de Dilo, en la tienda pública."}
                </p>
                {s[section].map((p, i) => (
                  <div className="variant-form" key={p.id || i}>
                    <Field
                      label={
                        section === "pages"
                          ? "Título de la página"
                          : "Título del artículo"
                      }
                      required
                      value={p.title}
                      onChange={(e) =>
                        set(
                          section,
                          s[section].map((x, j) =>
                            j === i ? { ...x, title: e.target.value } : x,
                          ),
                        )
                      }
                    />
                    <Field label="Contenido">
                      <textarea
                        required
                        style={{ minHeight: 170 }}
                        value={p.body}
                        onChange={(e) =>
                          set(
                            section,
                            s[section].map((x, j) =>
                              j === i ? { ...x, body: e.target.value } : x,
                            ),
                          )
                        }
                      />
                    </Field>
                    <Button
                      type="button"
                      kind="danger"
                      onClick={async () => {
                        if (
                          await askConfirm(
                            "¿Quitar este contenido? Se aplicará al guardar.",
                          )
                        )
                          set(
                            section,
                            s[section].filter((_, j) => j !== i),
                          );
                      }}
                    >
                      <Trash2 size={15} /> Quitar
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  kind="outline"
                  onClick={() =>
                    set(section, [
                      ...s[section],
                      { id: uid(), title: "", body: "" },
                    ])
                  }
                >
                  <Plus size={16} /> Añadir{" "}
                  {section === "pages" ? "página" : "artículo"}
                </Button>
              </>
            )}
            {section === "domain" && (
              <>
                <h3>Dirección de tu tienda</h3>
                <p>Tu tienda pública está en:</p>
                <div className="code-block">{location.origin}/</div>
                <div className="actions" style={{ margin: "20px 0" }}>
                  <Button
                    type="button"
                    kind="outline"
                    onClick={() =>
                      run(
                        () =>
                          navigator.clipboard.writeText(`${location.origin}/`),
                        "Dirección copiada",
                      ).catch(() => {})
                    }
                  >
                    <Copy size={16} /> Copiar enlace
                  </Button>
                  <a
                    className="btn outline"
                    href="/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ver tienda <ExternalLink size={16} />
                  </a>
                </div>
                <p>
                  Para utilizar un dominio propio, añádelo en Cloudflare Pages →
                  tu proyecto → Custom domains. Después actualiza las
                  direcciones de acceso y recuperación de Supabase y SITE_URL
                  para los correos.
                </p>
                <a
                  className="text-button"
                  href="https://developers.cloudflare.com/pages/configuration/custom-domains/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Abrir instrucciones de Cloudflare <ArrowUpRight size={15} />
                </a>
                <p className="notice">
                  Los dominios y las claves de conexión se configuran en el
                  proveedor. No se guardan secretos dentro de esta página.
                </p>
              </>
            )}
            {section !== "domain" && (
              <Button type="submit" disabled={busy} style={{ marginTop: 20 }}>
                <Save size={17} />
                {busy ? "Guardando…" : "Guardar configuración"}
              </Button>
            )}
          </div>
          <aside className="settings-preview">
            <Brand logo={s.logo} />
            <span className="eyebrow">ASÍ SE SENTIRÁ TU TIENDA</span>
            <h2>{s.headline}</h2>
            <p>{s.tagline}</p>
            <img
              src={safeUrl(s.banner) || "/assets/campana.png"}
              alt="Campaña"
            />
            <p style={{ fontSize: ".75rem", margin: "15px 0 0" }}>
              Abre Ver tienda después de guardar para comprobar la vista
              pública.
            </p>
          </aside>
        </div>
      </form>
    </>
  );
}
export function Team({ data, user, run }) {
  const [email, setEmail] = useState(""),
    [role, setRole] = useState("host"),
    [token, setToken] = useState(""),
    [busy, setBusy] = useState(false);
  const owner =
    data.members.find((m) => m.user_id === user.id)?.role === "owner";
  return (
    <>
      <PageHead
        eyebrow="VENDAN EN EQUIPO"
        title="Colaboradores y anfitriones"
        description="Cada persona ingresa con su propia cuenta y puede atender pedidos."
      />
      <div className="card table-wrap">
        <table>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Correo</th>
              <th>Rol</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.user_id}>
                <td>{m.name}</td>
                <td>{m.email}</td>
                <td>
                  {
                    {
                      owner: "Propietario",
                      admin: "Administrador",
                      host: "Anfitrión",
                    }[m.role]
                  }
                </td>
                <td>
                  {owner && m.role !== "owner" && (
                    <Button
                      kind="danger"
                      onClick={async () => {
                        if (
                          await askConfirm(`¿Quitar el acceso de ${m.email}?`)
                        )
                          run(() =>
                            api.removeMember(data.store.id, m.user_id),
                          ).catch(() => {});
                      }}
                    >
                      Quitar acceso
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {owner && (
        <div className="card" style={{ marginTop: 24 }}>
          <h3>Invitar colaborador</h3>
          <p className="notice">
            Administrador: configura tienda, productos y pedidos. Anfitrión:
            gestiona productos, inventario y pedidos. Solo el propietario puede
            conceder o quitar accesos.
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                const t = await run(
                  () => api.invite(data.store.id, email, role),
                  "Invitación creada",
                );
                setToken(t);
              } catch {
              } finally {
                setBusy(false);
              }
            }}
          >
            <div className="form-grid">
              <Field
                label="Correo del colaborador"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Field label="Permisos">
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="host">Anfitrión</option>
                  <option value="admin">Administrador</option>
                </select>
              </Field>
            </div>
            <Button disabled={busy}>
              <UserPlus size={17} /> Crear invitación
            </Button>
          </form>
          {token && (
            <>
              <p style={{ marginTop: 20 }}>
                Entrega este código al colaborador. Debe registrarse con{" "}
                <strong>{email}</strong>, confirmar su correo y pegarlo al
                ingresar. Vence en 7 días.
              </p>
              <div className="code-block">{token}</div>
              <Button
                kind="outline"
                style={{ marginTop: 15 }}
                onClick={() =>
                  run(
                    () => navigator.clipboard.writeText(token),
                    "Código copiado",
                  ).catch(() => {})
                }
              >
                <Copy size={16} /> Copiar código
              </Button>
            </>
          )}
        </div>
      )}
    </>
  );
}
export function Integrations({ data, run }) {
  const [connection, setConnection] = useState(null);
  const [checking, setChecking] = useState(false);
  async function checkConnection() {
    setChecking(true);
    try {
      const response = await fetch("/api/status", { cache: "no-store" });
      if (!response.ok) throw new Error();
      setConnection(await response.json());
    } catch {
      setConnection({ checkoutStatus: "unavailable" });
    } finally {
      setChecking(false);
    }
  }
  useEffect(() => { if (!demo) checkConnection(); }, []);
  const connectionHelp = {
    ready: "La conexión del servidor con la tienda está comprobada. El envío de correo se confirma en el estado de cada pedido.",
    invalid_configuration: "Una credencial guardada en Cloudflare está incompleta o contiene caracteres que no corresponden. Copia nuevamente la clave privada de Supabase en SUPABASE_SERVICE_ROLE_KEY, guarda en Production y vuelve a publicar.",
    missing_configuration: "Falta la URL de Supabase o la clave privada en Cloudflare Production.",
    database_auth_failed: "Supabase rechazó la credencial del servidor. Comprueba que SUPABASE_SERVICE_ROLE_KEY contiene la clave privada completa del mismo proyecto que SUPABASE_URL, guarda en Production y vuelve a publicar.",
    store_unavailable: "El servidor responde, pero no encontró la tienda publicada. Revisa el identificador de tienda y su publicación.",
    database_error: "Supabase respondió con un error. Revisa que las tablas y las actualizaciones de la base de datos estén instaladas.",
    unavailable: "No se pudo comprobar la conexión. Inténtalo de nuevo.",
  };
  return (
    <>
      <PageHead
        eyebrow="CONEXIONES DE TU TIENDA"
        title="Integraciones"
        description="Servicios que hacen funcionar el catálogo, los archivos, la seguridad y los correos."
      />
      {!demo && <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-head">
          <h3>Conexión para registrar pedidos</h3>
          <Button kind="outline" disabled={checking} onClick={checkConnection}>
            <RefreshCw size={16} /> {checking ? "Comprobando…" : "Comprobar conexión"}
          </Button>
        </div>
        <p role="status">{connection ? connectionHelp[connection.checkoutStatus] || connectionHelp.unavailable : "Comprobando la conexión del servidor…"}</p>
        {connection && <small>Correo: {connection.emailConfigured ? "configurado; entrega pendiente de comprobar" : "faltan datos de configuración"}{connection.version ? ` · Versión ${connection.version}` : ""}</small>}
      </div>}
      <div className="settings-grid">
        <div className="stack">
          {[
            {
              name: "Supabase",
              status: demo ? "Sin conectar · modo demostración" : "Conectado",
              text: "Cuentas de usuario, catálogo, pedidos, inventario y archivos. Se configura con la URL y la clave pública de tu proyecto.",
              url: "https://supabase.com/dashboard",
            },
            {
              name: "Cloudflare Pages",
              status: location.hostname.endsWith("pages.dev")
                ? "Cloudflare Pages"
                : location.hostname === "127.0.0.1"
                  ? "Vista previa local"
                  : "Dominio personalizado",
              text: "Aloja la web y ejecuta las funciones de compra, seguimiento y correo. Despliega el proyecto completo según la guía del ZIP.",
              url: "https://dash.cloudflare.com",
            },
            {
              name: "Correo transaccional · Resend",
              status: demo
                ? "Disponible al instalar"
                : "Ver estados de envío en cada pedido",
              text: "Resumen al comprador, copia del pedido nuevo a diloshopperu@gmail.com y actualizaciones de estado. Configura RESEND_API_KEY, MAIL_FROM y SITE_URL como secretos de Cloudflare. Usa un remitente verificado.",
              url: "https://resend.com/domains",
            },
            {
              name: "Verificación de compra · Turnstile",
              status: config.turnstileSiteKey
                ? "Clave pública configurada"
                : "Pendiente de configurar",
              text: "Protege las compras públicas. Configura la clave pública en config.js y la clave secreta en Cloudflare.",
              url: "https://dash.cloudflare.com/?to=/:account/turnstile",
            },
          ].map((c) => (
            <div className="card" key={c.name}>
              <div className="card-head">
                <h3>{c.name}</h3>
                <span className="status s-aceptado">{c.status}</span>
              </div>
              <p style={{ fontSize: ".85rem" }}>{c.text}</p>
              <a
                className="text-button"
                href={c.url}
                target="_blank"
                rel="noreferrer"
              >
                Abrir servicio <ExternalLink size={15} />
              </a>
            </div>
          ))}
        </div>
        <aside className="card">
          <h3>Datos y respaldo</h3>
          <p>
            Descarga un respaldo de tus registros. Guarda el archivo en un lugar
            privado: incluye información de clientes y enlaces de seguimiento.
          </p>
          <Button
            kind="outline"
            onClick={() =>
              download(
                JSON.stringify(data, null, 2),
                `dilo-respaldo-${new Date().toISOString().slice(0, 10)}.json`,
              )
            }
          >
            Descargar respaldo JSON
          </Button>
          <p className="file-note" style={{ marginTop: 16 }}>
            Este respaldo sirve para consulta y recuperación asistida. Las
            imágenes y videos deben respaldarse desde Supabase Storage.
          </p>
          {demo && (
            <div className="danger-zone card">
              <h3>Reiniciar demostración</h3>
              <p>
                Borra solamente los datos de prueba guardados en este navegador.
              </p>
              <Button
                kind="danger"
                onClick={async () => {
                  if (
                    await askConfirm(
                      "¿Borrar todos los cambios de esta demostración?",
                    )
                  )
                    run(
                      async () => api.resetDemo(),
                      "Demostración reiniciada",
                    ).catch(() => {});
                }}
              >
                <RefreshCw size={15} /> Reiniciar
              </Button>
            </div>
          )}
        </aside>
      </div>
    </>
  );
}
export function Marketing({ data, run }) {
  const offers = data.products.filter((p) => p.compare_price > p.price);
  return (
    <>
      <PageHead
        eyebrow="HAZ QUE TE ENCUENTREN"
        title="Promociones y marketing"
        description="Destaca tus ofertas y prepara la campaña de tu tienda."
        actions={
          <a className="btn" href="#admin/settings/appearance">
            Editar banner de campaña
          </a>
        }
      />
      <div className="dashboard-grid">
        <section className="card">
          <h3>Ofertas activas</h3>
          <p>
            El precio anterior se muestra tachado cuando es mayor que el precio
            de venta.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Antes</th>
                  <th>Ahora</th>
                  <th>Descuento</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{money(p.compare_price)}</td>
                    <td>{money(p.price)}</td>
                    <td>
                      {Math.round((1 - p.price / p.compare_price) * 100)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <a
            className="btn outline"
            style={{ marginTop: 20 }}
            href="#admin/editor"
          >
            Editar precios y ofertas
          </a>
        </section>
        <section className="card">
          <h3>Comparte tu tienda</h3>
          <p>Usa el enlace en tu biografía de Instagram, TikTok o WhatsApp.</p>
          <Button
            kind="outline"
            onClick={() =>
              run(
                () => navigator.clipboard.writeText(`${location.origin}/`),
                "Enlace copiado",
              ).catch(() => {})
            }
          >
            <Copy size={16} /> Copiar enlace de tienda
          </Button>
          <a
            href="#admin/settings/social"
            className="btn soft"
            style={{ marginTop: 14 }}
          >
            Configurar redes sociales
          </a>
        </section>
      </div>
    </>
  );
}
